import { useQueries } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { Currency } from '@/constants/currency'
import { queryKeys } from '@/constants/queryKeys'

import { getBankEligibilityOnboardingState } from '../api'
import { getAccountDetails } from '../features'
import { AccountDetails, AccountType } from '../features/Accounts/types'
import { BankEligibilityOnboardingState, Feature } from '../types'

import { useBusinessRole } from './useBusinessRole'
import { useFeature } from './useFeatures'
import { useWallets } from './useWallets'

export enum KYBApplicationState {
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_STARTED = 'NOT_STARTED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export function useKYBState(accountType: AccountType): KYBApplicationState {
  const { isAdmin } = useBusinessRole()

  const { isEnabled: isEURCWalletEnabled } = useFeature(Feature.EURC_WALLET)

  const { detailsPerCurrency } = useWallets({
    enabled: accountType === AccountType.EU_LOCAL,
  })

  const mainEURCWallet = detailsPerCurrency?.find(
    (detail) => detail.currency === Currency.EURC,
  )?.mainWalletId

  const [accountDetailsQuery, bankEligibilityOnboardingStateQuery] = useQueries(
    {
      queries: [
        {
          queryKey: [queryKeys.getAccountDetails, mainEURCWallet],
          queryFn: () => getAccountDetails({ id: mainEURCWallet ?? '' }),
          select: (data: AxiosResponse<AccountDetails>) => data?.data,
          enabled: !!mainEURCWallet,
        },
        {
          queryKey: [queryKeys.getBankEligibilityOnboardingState],
          queryFn: () =>
            getBankEligibilityOnboardingState(AccountType.EU_LOCAL),
          select: (data: AxiosResponse<BankEligibilityOnboardingState>) =>
            data.data,
          enabled: isAdmin && isEURCWalletEnabled,
          retry: 1,
        },
      ],
    },
  )

  switch (accountType) {
    case AccountType.EU_LOCAL: {
      const hasApplicationPending =
        !!bankEligibilityOnboardingStateQuery.data?.state

      const hasEURCBankAccountDetails =
        accountDetailsQuery.data?.bankAccounts.some(
          (account) => account.type === AccountType.EU_LOCAL,
        )

      if (hasEURCBankAccountDetails) {
        return KYBApplicationState.COMPLETED
      }

      if (hasApplicationPending) {
        return KYBApplicationState.IN_PROGRESS
      }

      return KYBApplicationState.NOT_STARTED
    }

    default:
      return KYBApplicationState.NOT_APPLICABLE
  }
}
